import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { AlertCircle, RefreshCw } from 'lucide-react';

interface ServiceWorkerUpdatePromptProps {
  registration: ServiceWorkerRegistration | null;
}

export function ServiceWorkerUpdatePrompt({ registration }: ServiceWorkerUpdatePromptProps) {
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [dismissedVersion, setDismissedVersion] = useState<string | null>(() => {
    // Get previously dismissed version from localStorage
    return localStorage.getItem('sw-dismissed-version');
  });

  useEffect(() => {
    if (!registration) return;

    // Check if there's already a waiting service worker
    if (registration.waiting) {
      // Get the version identifier (could be script URL or a timestamp)
      const waitingWorkerUrl = registration.waiting.scriptURL;
      const versionId = waitingWorkerUrl + '-' + registration.waiting.state;

      // Only show prompt if this version hasn't been dismissed
      if (dismissedVersion !== versionId) {
        setShowUpdatePrompt(true);
      }
    }

    // Listen for new service workers
    const handleUpdateFound = () => {
      const newWorker = registration.installing;

      if (newWorker) {
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && registration.waiting) {
            const waitingWorkerUrl = registration.waiting.scriptURL;
            const versionId = waitingWorkerUrl + '-' + registration.waiting.state;

            // Only show prompt if this version hasn't been dismissed
            if (dismissedVersion !== versionId) {
              setShowUpdatePrompt(true);
            }
          }
        });
      }
    };

    registration.addEventListener('updatefound', handleUpdateFound);

    // Listen for controlling service worker changes
    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!refreshing) {
        refreshing = true;
        window.location.reload();
      }
    });

    // Listen for messages from service worker
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data && event.data.type === 'UPDATE_AVAILABLE') {
        setShowUpdatePrompt(true);
      }
    });

    // Update checking disabled to prevent annoying notifications
    // const checkInterval = setInterval(() => {
    //   registration.update().catch(console.error);
    // }, 30000);

    return () => {
      registration.removeEventListener('updatefound', handleUpdateFound);
      // clearInterval(checkInterval);
    };
  }, [registration, dismissedVersion]);

  const handleUpdate = () => {
    if (!registration || !registration.waiting) return;

    setIsUpdating(true);

    // Clear the dismissed version since we're updating
    localStorage.removeItem('sw-dismissed-version');

    // Tell the waiting service worker to skip waiting
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
  };

  const handleDismiss = () => {
    if (registration?.waiting) {
      // Store the dismissed version identifier
      const waitingWorkerUrl = registration.waiting.scriptURL;
      const versionId = waitingWorkerUrl + '-' + registration.waiting.state;
      localStorage.setItem('sw-dismissed-version', versionId);
      setDismissedVersion(versionId);
    }
    setShowUpdatePrompt(false);
  };

  if (!showUpdatePrompt) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-md animate-in fade-in slide-in-from-bottom-4">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <AlertCircle className="h-5 w-5 text-blue-500" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              Update Available
            </h3>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              A new version of DomElec CRM is available. Update now to get the latest features and
              improvements.
            </p>
            <div className="mt-3 flex gap-2">
              <Button size="sm" onClick={handleUpdate} disabled={isUpdating} className="gap-2">
                {isUpdating ? (
                  <>
                    <RefreshCw className="h-3 w-3 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-3 w-3" />
                    Update Now
                  </>
                )}
              </Button>
              <Button size="sm" variant="outline" onClick={handleDismiss} disabled={isUpdating}>
                Later
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
